import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ukDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Moon, FileSignature, Phone, Mail } from "lucide-react";

const STATUSES = ["new", "contacted", "booked", "declined"];

function EveningList({ items, onStatus }) {
  if (!items.length) return <p className="text-sm text-stone-500">No earlier or evening requests yet.</p>;
  return items.map((r) => (
    <div key={r.id} data-testid={`evening-request-${r.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold">{r.name} <span className="text-xs text-stone-400 font-normal">· {ukDate(r.created_at)}</span></div>
        <div className="text-sm text-stone-600">{r.service_name || "Treatment not chosen"}{r.preferred_dates && ` · ${r.preferred_dates}`}</div>
        <div className="text-xs text-stone-500 mt-1 flex gap-3 flex-wrap"><span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.mobile}</span><span className="flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</span></div>
        {r.notes && <p className="text-sm text-stone-600 mt-1">{r.notes}</p>}
      </div>
      <select data-testid={`evening-status-${r.id}`} value={r.status} onChange={(e) => onStatus(r.id, e.target.value)} className="h-9 rounded-full border border-stone-300 px-3 text-sm bg-white capitalize">
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  ));
}

function ConsentView({ form, onClose }) {
  return (
    <Dialog open={!!form} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        {form && (<>
          <DialogHeader><DialogTitle>{form.client_name} — {form.service_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-stone-500">Signed {ukDate(form.created_at)}</p>
          {form.consent && (<div className="space-y-1.5 text-sm">
            {Object.entries(form.consent.answers || {}).map(([q, a]) => (
              <div key={q} className="flex justify-between gap-3"><span className="text-stone-600">{q}</span><b className={a === "Yes" ? "text-rose-700" : ""}>{a}</b></div>))}
            {form.consent.details && <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2"><b>Details:</b> {form.consent.details}</div>}
            <img src={form.consent.signature} alt="Client signature" className="mt-2 border rounded-lg bg-white h-24" />
          </div>)}
          {form.guardian && (<div className="text-sm mt-3"><b>Parent / guardian:</b> {form.guardian.name}<img src={form.guardian.signature} alt="Guardian signature" className="mt-2 border rounded-lg bg-white h-24" /></div>)}
        </>)}
      </DialogContent>
    </Dialog>
  );
}

export default function Inbox() {
  const [evening, setEvening] = useState([]);
  const [forms, setForms] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    const [e, f] = await Promise.all([api.get("/evening-requests"), api.get("/consent-forms")]);
    setEvening(e.data); setForms(f.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const onStatus = async (id, status) => { await api.patch(`/evening-requests/${id}`, { status }); toast.success("Updated"); load(); };

  return (
    <div className="max-w-4xl">
      <h1 className="font-extrabold text-3xl">Requests & forms</h1>
      <p className="text-stone-600 text-sm mt-1">Earlier or evening appointment requests and signed consent forms</p>
      <Tabs defaultValue="evening" className="mt-5">
        <TabsList className="rounded-full">
          <TabsTrigger value="evening" data-testid="inbox-tab-evening" className="rounded-full"><Moon className="h-3.5 w-3.5 mr-1" />Earlier / evening requests ({evening.filter((e) => e.status === "new").length})</TabsTrigger>
          <TabsTrigger value="forms" data-testid="inbox-tab-forms" className="rounded-full"><FileSignature className="h-3.5 w-3.5 mr-1" />Consent forms ({forms.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="evening" className="mt-5 space-y-3"><EveningList items={evening} onStatus={onStatus} /></TabsContent>
        <TabsContent value="forms" className="mt-5 space-y-2">
          {forms.length === 0 && <p className="text-sm text-stone-500">No consent forms yet.</p>}
          {forms.map((f) => (
            <button key={f.id} data-testid={`consent-form-${f.id}`} onClick={() => setView(f)} className="w-full text-left bg-white rounded-xl border border-stone-200 px-4 py-3 shadow-sm flex justify-between text-sm hover:border-stone-900">
              <span><b>{f.client_name}</b> · {f.service_name}{f.guardian && " · guardian consent"}</span><span className="text-stone-500">{ukDate(f.created_at)}</span>
            </button>
          ))}
        </TabsContent>
      </Tabs>
      <ConsentView form={view} onClose={() => setView(null)} />
    </div>
  );
}
